import { spaceAPI } from '@/api';
import HeaderComponent from './AvnfrGGHeader';
import {
  Content,
  LoadingMessage,
  SectionContainer,
  SectionContent,
  SectionHeader,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper,
} from '@/components/CommonStyles';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import { palette } from '@/modules/defines/styles';
import '@/modules/defines/font.css';

const pageName = {
  '/': '초기 화면 (/)',
  '/main': '메인 화면 (/main)',
  '/navi': '길찾기 (/navi)',
  '/xr-navi': 'XR 길찾기 (/xr-navi)',
  '/parking': '주차 등록 (/parking)',
};

const AvnfrGGDataContainer = () => {
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('monthly');
  const [expandedLegends, setExpandedLegends] = useState<{
    [key: number]: boolean;
  }>({});
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<{ [key: number]: string[] }>(
    {}
  );
  const [rankingData, setRankingData] = useState<{
    departure: { [key: string]: number };
    arrival: { [key: string]: number };
  }>({ departure: {}, arrival: {} });

  // LOAD Data
  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: ['getStatisticsAvnfrGG'],
    queryFn: () =>
      spaceAPI.getStatisticsAvnfrGG({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const toggleLegend = (chartIndex: number) => {
    setExpandedLegends((prev) => ({
      ...prev,
      [chartIndex]: !prev[chartIndex],
    }));
  };

  const handleLegendHover = (seriesName: string, chartIndex: number) => {
    setHoveredSeries(seriesName);

    // 차트 컨테이너에 하이라이트 클래스 추가
    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.add('series-highlighted');

      // 해당 시리즈에 highlighted 클래스 추가
      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series, index) => {
        const currentChart = chartData?.[chartIndex];
        if (currentChart && currentChart.series[index]?.name === seriesName) {
          series.classList.add('highlighted');
        } else {
          series.classList.remove('highlighted');
        }
      });
    });
  };

  const handleLegendLeave = (chartIndex: number) => {
    setHoveredSeries(null);

    // 차트 컨테이너에서 하이라이트 클래스 제거
    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.remove('series-highlighted');

      // 모든 시리즈에서 highlighted 클래스 제거
      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series) => {
        series.classList.remove('highlighted');
      });
    });
  };

  // 1) ApexCharts 옵션 (Bar Chart)
  const chartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x' as const,
        autoScaleYaxis: true,
      },
      selection: {
        enabled: true,
        type: 'x' as const,
      },
      pan: {
        enabled: true,
        type: 'x' as const,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val;
      },
      style: {
        fontSize: '11px',
        fontWeight: '600',
      },
    },
    stroke: {
      show: false,
      width: 1,
      colors: ['transparent'],
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '페이지 뷰',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      y: {
        formatter: function (val: number) {
          return val + ' 회';
        },
      },
    },
  };

  // 2) ApexCharts 옵션 (Line Chart)
  const lineChartOptions = {
    chart: {
      type: 'line' as const,
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      title: {
        text: '날짜',
      },
    },
    yaxis: {
      title: {
        text: '페이지 뷰',
      },
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      // y: {
      //   formatter: function (val: number) {
      //     return val + ' 회';
      //   },
      // },
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];

        // 값이 0인 경우 tooltip을 표시하지 않음
        if (value === 0) {
          return '';
        }

        // 해당 데이터 포인트의 모든 시리즈 값을 확인
        let tooltipContent = `<div class="apexcharts-tooltip-box" style="background: rgba(0, 0, 0, 0.8); color: white; padding: 8px; border-radius: 4px; font-size: 12px; line-height: 1.5;">`;

        // 값이 0이 아닌 시리즈들을 수집하고 정렬
        const validSeries = [];
        for (let i = 0; i < series.length; i++) {
          const seriesValue = series[i][dataPointIndex];
          if (seriesValue > 0) {
            validSeries.push({
              name: pageName[w.globals.seriesNames[i] as keyof typeof pageName],
              value: seriesValue,
            });
          }
        }

        // 값 순으로 내림차순 정렬 (큰 값부터)
        validSeries.sort((a, b) => b.value - a.value);

        // 정렬된 순서로 tooltip에 표시
        validSeries.forEach((item) => {
          tooltipContent += `<span>${item.name} : <strong style="font-weight: 800">${item.value} 회</strong></span><br/>`;
        });

        tooltipContent += `</div>`;
        return tooltipContent;
      },
    },
  };

  // 3, 4) ApexCharts 옵션 (Line Chart) - 길찾기용
  const lineChartOptions2 = {
    chart: {
      type: 'line' as const,
      height: 300,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 2,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '횟수',
      },
    },
    legend: {
      show: false, // 기본 범례 숨기고 커스텀 범례 사용
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];

        // 값이 0인 경우 tooltip을 표시하지 않음
        if (value === 0) {
          return '';
        }

        // 해당 데이터 포인트의 모든 시리즈 값을 확인
        let tooltipContent = `<div class="apexcharts-tooltip-box" style="background: rgba(0, 0, 0, 0.8); color: white; padding: 8px; border-radius: 4px; font-size: 10px; line-height: 1.2;">`;

        // 값이 0이 아닌 시리즈들을 수집하고 정렬
        const validSeries = [];
        for (let i = 0; i < series.length; i++) {
          const seriesValue = series[i][dataPointIndex];
          if (seriesValue > 0) {
            validSeries.push({
              name: w.globals.seriesNames[i],
              value: seriesValue,
            });
          }
        }

        // 값 순으로 내림차순 정렬 (큰 값부터)
        validSeries.sort((a, b) => b.value - a.value);

        // 정렬된 순서로 tooltip에 표시 (최대 16개로 제한)
        const maxItems = 16;
        const itemsToShow = validSeries.slice(0, maxItems);
        const hasMoreItems = validSeries.length > maxItems;

        itemsToShow.forEach((item) => {
          tooltipContent += `<span>${item.name}: <strong style="font-weight: 800">${item.value} 회</strong></span><br/>`;
        });

        if (hasMoreItems) {
          tooltipContent += `<span style="color: #888; font-style: italic;">... 외 ${
            validSeries.length - maxItems
          }개 항목</span><br/>`;
        }

        tooltipContent += `</div>`;
        return tooltipContent;
      },
    },
  };

  const aggregateMonthlyData = (dates: string[], data: number[]) => {
    const monthlyMap: { [key: string]: number } = {};

    dates.forEach((date, index) => {
      const month = date.substring(0, 7); // "2025-10" 형식
      if (!monthlyMap[month]) {
        monthlyMap[month] = 0;
      }
      monthlyMap[month] += data[index];
    });

    const sortedMonths = Object.keys(monthlyMap).sort();
    const monthlyData = sortedMonths.map((month) => monthlyMap[month]);

    return {
      categories: sortedMonths,
      data: monthlyData,
    };
  };

  // CHART DATA
  const chartData = useMemo(() => {
    if (!dataAPI?.data) return null;

    const { dates, data1, data2, data3, data4, data5 } = dataAPI.data;

    const cutoffDate = new Date('2025-08-03');
    const cutoffIndex = dates.findIndex((dateStr: string) => {
      const itemDate = new Date(dateStr + 'T00:00:00');
      return itemDate >= cutoffDate;
    });

    if (cutoffIndex === -1) {
      console.log('chartData: No data after cutoff date');
      return null;
    }
    const filteredDates = dates.slice(cutoffIndex);
    const filteredData1 = data1.slice(cutoffIndex);
    const filteredData2 = data2.map((data: any) => ({
      name: data.label,
      data: data.value.slice(cutoffIndex),
    }));
    const filteredData3 = data3.map((data: any) => {
      const slicedValues = data.value.slice(cutoffIndex);
      let modifiedValues = [...slicedValues];

      // 특정 데이터 수정: '마호가니' 8/25 값을 32로 설정
      if (data.label === '마호가니') {
        // 8/25에 해당하는 인덱스 찾기 (날짜 배열에서 '2025-08-25' 찾기)
        const targetDate = '2025-08-25';
        const targetIndex = filteredDates.findIndex(
          (date: string) => date === targetDate
        );
        if (targetIndex >= 0 && targetIndex < modifiedValues.length) {
          modifiedValues[targetIndex] = 32;
        }
      }

      const sum = modifiedValues.reduce(
        (acc: number, val: number) => acc + val,
        0
      );

      return {
        name: data.label,
        data: modifiedValues,
        total: sum,
      };
    });
    const filteredData4 = data4.map((data: any) => {
      const slicedValues = data.value.slice(cutoffIndex);
      let modifiedValues = [...slicedValues];

      // 모든 라벨 확인용 로그
      console.log('도착 위치 데이터 라벨:', data.label);

      // 특정 데이터 수정: '플레어비' 8/25 값을 24로 설정
      if (data.label && data.label.includes('플레어비')) {
        // 8/25에 해당하는 인덱스 찾기 (날짜 배열에서 '2025-08-25' 찾기)
        const targetDate = '2025-08-25';
        const targetIndex = filteredDates.findIndex(
          (date: string) => date === targetDate
        );
        if (targetIndex >= 0 && targetIndex < modifiedValues.length) {
          console.log('플레어비 8/25 데이터 수정 중:', {
            label: data.label,
            targetDate: targetDate,
            originalValue: modifiedValues[targetIndex],
            targetIndex: targetIndex,
          });
          modifiedValues[targetIndex] = 24;
          console.log(
            '플레어비 8/25 값 수정 완료:',
            modifiedValues[targetIndex]
          );
        }
      }

      const sum = modifiedValues.reduce(
        (acc: number, val: number) => acc + val,
        0
      );

      return {
        name: data.label,
        data: modifiedValues,
        total: sum,
      };
    });
    const filteredData5 = data5.slice(cutoffIndex);

    // 순위 데이터 계산 및 상태 업데이트
    const departureRanking: { [key: string]: number } = {};
    const arrivalRanking: { [key: string]: number } = {};

    filteredData3.forEach((item: any) => {
      departureRanking[item.name] = item.total;
    });

    filteredData4.forEach((item: any) => {
      arrivalRanking[item.name] = item.total;
    });

    setRankingData({
      departure: departureRanking,
      arrival: arrivalRanking,
    });

    let chart1Categories = filteredDates;
    let chart1Data = filteredData1;

    if (viewType === 'monthly') {
      const monthlyResult = aggregateMonthlyData(filteredDates, filteredData1);
      chart1Categories = monthlyResult.categories;
      chart1Data = monthlyResult.data;
    }

    const result = [
      {
        id: 1,
        title: '서비스 이용자 수',
        description: ' ',
        categories: chart1Categories,
        series: [
          {
            name: '페이지 뷰',
            data: chart1Data,
          },
        ],
        options: chartOptions,
        type: 'bar',
      },
      {
        id: 2,
        title: '화면별 접속자 수',
        description: ' ',
        categories: filteredDates,
        series: filteredData2,
        options: lineChartOptions,
        type: 'line',
      },
      {
        id: 3,
        title: '길찾기 출발 위치',
        description: ' ',
        categories: filteredDates,
        series: filteredData3,
        options: lineChartOptions2,
        type: 'line',
      },
      {
        id: 4,
        title: '길찾기 도착 위치',
        description: ' ',
        categories: filteredDates,
        series: filteredData4,
        options: lineChartOptions2,
        type: 'line',
      },
      {
        id: 5,
        title: '주차 등록자 수',
        description: ' ',
        categories: filteredDates,
        series: [
          {
            name: '주차자리 등록 횟수',
            data: filteredData5,
          },
        ],
        options: chartOptions,
        type: 'bar',
      },
    ];

    return result;
  }, [dataAPI?.data, viewType]);

  // 범례 데이터 정렬 함수
  const getSortedLegendData = (chartIndex: number) => {
    if (!chartData) return [];

    const chart = chartData[chartIndex];
    if (!chart || chartIndex < 2 || chartIndex > 3) return [];

    const seriesData = chart.series;
    const legendData = seriesData.map((series: any, index: number) => {
      const totalValue = series.data.reduce(
        (sum: number, val: number) => sum + val,
        0
      );
      return {
        name: series.name,
        total: totalValue,
        color: `hsl(${(index * 137.5) % 360}, 70%, 50%)`, // 색상 분포
      };
    });

    // 클릭수 합산 기준 내림차순 정렬
    return legendData.sort((a: any, b: any) => b.total - a.total);
  };

  // 필터링된 시리즈 데이터 생성 (색상 보존)
  const getFilteredSeries = (chartIndex: number) => {
    if (!chartData) return [];

    const chart = chartData[chartIndex];
    if (!chart) return [];

    const defaultColors = [
      '#008FFB',
      '#00E396',
      '#FEB019',
      '#FF4560',
      '#775DD0',
      '#546E7A',
      '#26a69a',
      '#D10CE8',
      '#FF9800',
      '#795548',
    ];

    const hiddenSeriesForChart = hiddenSeries[chartIndex] || [];
    return chart.series
      .filter((series: any) => !hiddenSeriesForChart.includes(series.name))
      .map((series: any, filteredIndex: number) => {
        // 원래 시리즈의 인덱스를 찾아서 해당 색상 사용
        const originalIndex = chart.series.findIndex(
          (s: any) => s.name === series.name
        );
        return {
          ...series,
          color: defaultColors[originalIndex % defaultColors.length],
        };
      });
  };

  // 차트용 데이터 슬라이싱 함수 (bar, line 공통)
  const getChartData = (chart: any) => {
    if (!chart) {
      return {
        categories: chart?.categories || [],
        series: chart?.series || [],
        needsScroll: false,
      };
    }

    const totalLength = chart.categories.length;

    // 14개보다 적으면 모든 데이터 표시
    if (totalLength <= 14) {
      return {
        categories: chart.categories,
        series: chart.series,
        needsScroll: false,
      };
    }

    // 전체 데이터를 유지하되 스크롤 가능하도록 설정
    return {
      categories: chart.categories,
      series: chart.series,
      needsScroll: true,
      visibleWidth: `${(14 / totalLength) * 100}%`,
      scrollLeft: `${((totalLength - 14) / totalLength) * 100}%`,
    };
  };

  return (
    <Container>
      <HeaderComponent />
      <Content>
        {isLoadingAPI ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : !dataAPI?.data ? (
          <LoadingMessage>
            데이터가 없습니다. 잠시 후 다시 시도해주세요.
          </LoadingMessage>
        ) : (
          chartData && (
            <SectionWrapper>
              {chartData.map((chart, index) => (
                <SectionContainer key={chart.id}>
                  {index === 0 ? (
                    <CustomSectionHeader>
                      <TitleContainer>
                        <SectionTitle>{chart.title}</SectionTitle>
                        <SectionSubTitle>{chart.description}</SectionSubTitle>
                      </TitleContainer>
                      <TabContainer>
                        <Tab
                          active={viewType === 'monthly'}
                          onClick={() => setViewType('monthly')}
                        >
                          월별
                        </Tab>
                        <Tab
                          active={viewType === 'daily'}
                          onClick={() => setViewType('daily')}
                        >
                          일별
                        </Tab>
                      </TabContainer>
                    </CustomSectionHeader>
                  ) : (
                    <SectionHeader>
                      <SectionTitle>{chart.title}</SectionTitle>
                      <SectionSubTitle>{chart.description}</SectionSubTitle>
                    </SectionHeader>
                  )}
                  <SectionContent>
                    {index === 2 || index === 3 ? (
                      <NaviLayout>
                        <RankingContainer>
                          <RankingList>
                            {(() => {
                              const data =
                                index === 2
                                  ? rankingData.departure
                                  : rankingData.arrival;
                              const sortedEntries = Object.entries(data)
                                .sort(
                                  (
                                    [, a]: [string, any],
                                    [, b]: [string, any]
                                  ) => (b as number) - (a as number)
                                )
                                .slice(0, 8);

                              return sortedEntries.map(([key, value], idx) => (
                                <RankingItem key={idx} rank={idx + 1}>
                                  <RankingNumber rank={idx + 1}>
                                    {idx + 1}
                                  </RankingNumber>
                                  <RankingContent>
                                    <RankingName rank={idx + 1}>
                                      {key}
                                    </RankingName>
                                    <RankingCount rank={idx + 1}>
                                      {value as number}회
                                    </RankingCount>
                                  </RankingContent>
                                </RankingItem>
                              ));
                            })()}
                          </RankingList>
                        </RankingContainer>
                        <ChartSection>
                          {(() => {
                            const chartData = getChartData(chart);

                            if (chartData.needsScroll) {
                              return (
                                <ChartWithFixedTitles>
                                  <FixedAxisTitles>
                                    {chart.options.xaxis?.title?.text && (
                                      <XAxisTitle>
                                        {chart.options.xaxis.title.text}
                                      </XAxisTitle>
                                    )}
                                    {chart.options.yaxis?.title?.text && (
                                      <YAxisTitle>
                                        {chart.options.yaxis.title.text}
                                      </YAxisTitle>
                                    )}
                                  </FixedAxisTitles>
                                  <ScrollableChartWrapper
                                    className={`chart-container-${index}`}
                                    ref={(ref) => {
                                      if (ref) {
                                        setTimeout(() => {
                                          ref.scrollLeft =
                                            ref.scrollWidth * 0.5;
                                        }, 100);
                                      }
                                    }}
                                  >
                                    <ScrollableChart>
                                      <ReactApexChart
                                        options={
                                          {
                                            ...chart.options,
                                            chart: {
                                              ...chart.options.chart,
                                              width: '200%',
                                              toolbar: {
                                                show: false,
                                              },
                                            } as any,
                                            xaxis: {
                                              ...chart.options.xaxis,
                                              categories: chartData.categories,
                                              title: {
                                                ...chart.options.xaxis?.title,
                                                text: undefined,
                                              },
                                            },
                                            yaxis: {
                                              ...chart.options.yaxis,
                                              title: {
                                                ...chart.options.yaxis?.title,
                                                text: undefined,
                                              },
                                              labels: {
                                                show: true,
                                                style: {
                                                  fontSize: '11px',
                                                  fontWeight: '400',
                                                  colors: ['#6B7280'],
                                                },
                                              },
                                            },
                                            stroke: {
                                              curve: 'smooth',
                                              width: 2,
                                            },
                                            states: {
                                              hover: {
                                                filter: {
                                                  type: 'none',
                                                },
                                              },
                                              active: {
                                                allowMultipleDataPointsSelection:
                                                  false,
                                                filter: {
                                                  type: 'none',
                                                },
                                              },
                                            },
                                            legend: {
                                              show: false,
                                            },
                                          } as any
                                        }
                                        series={getFilteredSeries(index)}
                                        type={chart.type as any}
                                        height={300}
                                      />
                                    </ScrollableChart>
                                  </ScrollableChartWrapper>
                                  <FixedLegendContainer>
                                    <CustomLegendContainer>
                                      <LegendHeader
                                        expanded={expandedLegends[index]}
                                        onClick={() => toggleLegend(index)}
                                      >
                                        <LegendTitle>범례</LegendTitle>
                                        <LegendToggle
                                          expanded={expandedLegends[index]}
                                        >
                                          {expandedLegends[index] ? '▼' : '▶'}
                                        </LegendToggle>
                                      </LegendHeader>
                                      <LegendContent
                                        expanded={expandedLegends[index]}
                                      >
                                        <LegendGrid>
                                          {getSortedLegendData(index).map(
                                            (item: any, idx: number) => (
                                              <LegendItem
                                                key={idx}
                                                onMouseEnter={() =>
                                                  handleLegendHover(
                                                    item.name,
                                                    index
                                                  )
                                                }
                                                onMouseLeave={() =>
                                                  handleLegendLeave(index)
                                                }
                                                isHovered={
                                                  hoveredSeries === item.name
                                                }
                                              >
                                                <LegendColor
                                                  color={item.color}
                                                />
                                                <LegendName>
                                                  {item.name}
                                                </LegendName>
                                              </LegendItem>
                                            )
                                          )}
                                        </LegendGrid>
                                      </LegendContent>
                                    </CustomLegendContainer>
                                  </FixedLegendContainer>
                                </ChartWithFixedTitles>
                              );
                            }

                            return (
                              <>
                                <ChartContainer
                                  className={`chart-container-${index}`}
                                >
                                  <ReactApexChart
                                    options={
                                      {
                                        ...chart.options,
                                        stroke: {
                                          curve: 'smooth',
                                          width: 2,
                                        },
                                        states: {
                                          hover: {
                                            filter: {
                                              type: 'none',
                                            },
                                          },
                                          active: {
                                            allowMultipleDataPointsSelection:
                                              false,
                                            filter: {
                                              type: 'none',
                                            },
                                          },
                                        },
                                        xaxis: {
                                          ...chart.options.xaxis,
                                          categories: chart.categories,
                                        },
                                        yaxis: {
                                          ...chart.options.yaxis,
                                          labels: {
                                            show: true,
                                            style: {
                                              fontSize: '11px',
                                              fontWeight: '400',
                                              colors: ['#6B7280'],
                                            },
                                          },
                                        },
                                      } as any
                                    }
                                    series={getFilteredSeries(index)}
                                    type={chart.type as any}
                                    height={300}
                                  />
                                </ChartContainer>
                                <CustomLegendContainer>
                                  <LegendHeader
                                    expanded={expandedLegends[index]}
                                    onClick={() => toggleLegend(index)}
                                  >
                                    <LegendTitle>범례</LegendTitle>
                                    <LegendToggle
                                      expanded={expandedLegends[index]}
                                    >
                                      {expandedLegends[index] ? '▼' : '▶'}
                                    </LegendToggle>
                                  </LegendHeader>
                                  <LegendContent
                                    expanded={expandedLegends[index]}
                                  >
                                    <LegendGrid>
                                      {getSortedLegendData(index).map(
                                        (item: any, idx: number) => (
                                          <LegendItem
                                            key={idx}
                                            onMouseEnter={() =>
                                              handleLegendHover(
                                                item.name,
                                                index
                                              )
                                            }
                                            onMouseLeave={() =>
                                              handleLegendLeave(index)
                                            }
                                            isHovered={
                                              hoveredSeries === item.name
                                            }
                                          >
                                            <LegendColor color={item.color} />
                                            <LegendName>{item.name}</LegendName>
                                          </LegendItem>
                                        )
                                      )}
                                    </LegendGrid>
                                  </LegendContent>
                                </CustomLegendContainer>
                              </>
                            );
                          })()}
                        </ChartSection>
                      </NaviLayout>
                    ) : (
                      <>
                        {(() => {
                          const chartData = getChartData(chart);

                          if (chartData.needsScroll) {
                            return (
                              <ChartWithFixedTitles>
                                <FixedAxisTitles>
                                  {chart.options.xaxis?.title?.text && (
                                    <XAxisTitle>
                                      {chart.options.xaxis.title.text}
                                    </XAxisTitle>
                                  )}
                                  {chart.options.yaxis?.title?.text && (
                                    <YAxisTitle>
                                      {chart.options.yaxis.title.text}
                                    </YAxisTitle>
                                  )}
                                </FixedAxisTitles>
                                <ScrollableChartWrapper
                                  className={`chart-container-${index}`}
                                  ref={(ref) => {
                                    if (ref) {
                                      // 최근 데이터가 보이도록 스크롤 위치 설정
                                      setTimeout(() => {
                                        ref.scrollLeft = ref.scrollWidth * 0.5; // 오른쪽 절반 지점으로 스크롤
                                      }, 100);
                                    }
                                  }}
                                >
                                  <ScrollableChart
                                    $isBarChart={chart.type === 'bar'}
                                  >
                                    <ReactApexChart
                                      options={
                                        {
                                          ...chart.options,
                                          chart: {
                                            ...chart.options.chart,
                                            width:
                                              chart.type === 'bar'
                                                ? '300%'
                                                : '200%', // bar 차트는 더 넓게
                                            toolbar: {
                                              show: false,
                                            },
                                          } as any,
                                          xaxis: {
                                            ...chart.options.xaxis,
                                            categories: chartData.categories,
                                            title: {
                                              ...chart.options.xaxis?.title,
                                              text: undefined, // 원래 축 제목 숨김
                                            },
                                          },
                                          yaxis: {
                                            ...chart.options.yaxis,
                                            title: {
                                              ...chart.options.yaxis?.title,
                                              text: undefined, // 원래 축 제목 숨김
                                            },
                                            labels: {
                                              show: true,
                                              style: {
                                                fontSize: '11px',
                                                fontWeight: '400',
                                                colors: ['#6B7280'],
                                              },
                                            },
                                          },
                                          stroke: {
                                            curve: 'smooth',
                                            width: 2,
                                          },
                                          states: {
                                            hover: {
                                              filter: {
                                                type: 'none',
                                              },
                                            },
                                            active: {
                                              allowMultipleDataPointsSelection:
                                                false,
                                              filter: {
                                                type: 'none',
                                              },
                                            },
                                          },
                                          legend: {
                                            show: false, // 기본 범례 숨김
                                          },
                                          plotOptions:
                                            chart.type === 'bar'
                                              ? {
                                                  ...(chart.options as any)
                                                    ?.plotOptions,
                                                  bar: {
                                                    ...(chart.options as any)
                                                      ?.plotOptions?.bar,
                                                    columnWidth: '40%', // 더 넓은 컬럼
                                                  },
                                                }
                                              : (chart.options as any)
                                                  ?.plotOptions || {},
                                        } as any
                                      }
                                      series={getFilteredSeries(index)}
                                      type={chart.type as any}
                                      height={350}
                                    />
                                  </ScrollableChart>
                                </ScrollableChartWrapper>
                                {chart.type === 'line' && (
                                  <FixedLegendContainer>
                                    <LineChartLegendContainer>
                                      {chart.series.map(
                                        (series: any, seriesIndex: number) => {
                                          const hiddenSeriesForChart =
                                            hiddenSeries[index] || [];
                                          const isHidden =
                                            hiddenSeriesForChart.includes(
                                              series.name
                                            );
                                          const isHovered =
                                            hoveredSeries === series.name;

                                          // ApexCharts 기본 색상 팔레트
                                          const defaultColors = [
                                            '#008FFB',
                                            '#00E396',
                                            '#FEB019',
                                            '#FF4560',
                                            '#775DD0',
                                            '#546E7A',
                                            '#26a69a',
                                            '#D10CE8',
                                            '#FF9800',
                                            '#795548',
                                          ];

                                          // 원본 시리즈 인덱스를 사용하여 색상 일관성 유지
                                          const originalIndex =
                                            chart.series.findIndex(
                                              (s: any) => s.name === series.name
                                            );
                                          const seriesColor =
                                            defaultColors[
                                              originalIndex %
                                                defaultColors.length
                                            ];

                                          return (
                                            <LineChartLegendItem
                                              key={seriesIndex}
                                              isHovered={isHovered}
                                              isHidden={isHidden}
                                              seriesColor={seriesColor}
                                              onMouseEnter={() =>
                                                handleLegendHover(
                                                  series.name,
                                                  index
                                                )
                                              }
                                              onMouseLeave={() =>
                                                handleLegendLeave(index)
                                              }
                                              onClick={() => {
                                                const currentHidden =
                                                  hiddenSeries[index] || [];
                                                const isCurrentlyHidden =
                                                  currentHidden.includes(
                                                    series.name
                                                  );

                                                if (isCurrentlyHidden) {
                                                  // 시리즈 보이기
                                                  setHiddenSeries((prev) => ({
                                                    ...prev,
                                                    [index]:
                                                      currentHidden.filter(
                                                        (name) =>
                                                          name !== series.name
                                                      ),
                                                  }));
                                                } else {
                                                  // 시리즈 숨기기
                                                  setHiddenSeries((prev) => ({
                                                    ...prev,
                                                    [index]: [
                                                      ...currentHidden,
                                                      series.name,
                                                    ],
                                                  }));
                                                }
                                              }}
                                            >
                                              <LineChartLegendColor
                                                color={seriesColor}
                                              />
                                              <LineChartLegendName>
                                                {pageName[
                                                  series.name as keyof typeof pageName
                                                ] || series.name}
                                              </LineChartLegendName>
                                            </LineChartLegendItem>
                                          );
                                        }
                                      )}
                                    </LineChartLegendContainer>
                                  </FixedLegendContainer>
                                )}
                              </ChartWithFixedTitles>
                            );
                          }

                          return (
                            <ChartContainer
                              className={`chart-container-${index}`}
                            >
                              <ReactApexChart
                                options={
                                  {
                                    ...chart.options,
                                    stroke: {
                                      curve: 'smooth',
                                      width: 2,
                                    },
                                    states: {
                                      hover: {
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                      active: {
                                        allowMultipleDataPointsSelection: false,
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                    },
                                    chart: {
                                      ...chart.options.chart,
                                      toolbar: {
                                        ...chart.options.chart?.toolbar,
                                        show: chart.type === 'bar',
                                      },
                                    } as any,
                                    xaxis: {
                                      ...chart.options.xaxis,
                                      categories: chartData.categories,
                                    },
                                    yaxis: {
                                      ...chart.options.yaxis,
                                      labels: {
                                        show: true,
                                        style: {
                                          fontSize: '11px',
                                          fontWeight: '400',
                                          colors: ['#6B7280'],
                                        },
                                      },
                                    },
                                  } as any
                                }
                                series={chartData.series}
                                type={chart.type as any}
                                height={350}
                              />
                            </ChartContainer>
                          );
                        })()}
                      </>
                    )}
                  </SectionContent>
                </SectionContainer>
              ))}
            </SectionWrapper>
          )
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChartContainer = styled.div`
  width: 100%;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  /* ApexCharts 툴팁 z-index 최고로 설정 */
  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  /* 시리즈 하이라이트 효과 */
  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  /* ApexCharts 툴바 스타일링 */
  .apexcharts-toolbar {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 8px;
  }

  .apexcharts-toolbar-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  .apexcharts-pan-icon,
  .apexcharts-download-icon,
  .apexcharts-zoom-icon,
  .apexcharts-zoomin-icon,
  .apexcharts-zoomout-icon,
  .apexcharts-reset-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  /* 차트 영역 스타일링 */
  .apexcharts-canvas {
    overflow: visible;
  }

  /* 줌/팬 관련 스타일링 */
  .apexcharts-zoomable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  /* 선택 영역 스타일링 */
  .apexcharts-selection-rect {
    fill: rgba(59, 130, 246, 0.1);
    stroke: #3b82f6;
    stroke-width: 1;
    stroke-dasharray: 4 4;
  }
`;

const ScrollableChartWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 8px;

  /* ApexCharts 툴팁 z-index 최고로 설정 */
  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  /* 시리즈 하이라이트 효과 */
  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    border-radius: 6px;
    border: 2px solid #f1f5f9;

    &:hover {
      background: linear-gradient(90deg, #94a3b8, #64748b);
    }
  }

  /* Firefox 스크롤바 */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const ScrollableChart = styled.div<{ $isBarChart?: boolean }>`
  width: ${({ $isBarChart }) => ($isBarChart ? '300%' : '200%')};
  min-width: ${({ $isBarChart }) => ($isBarChart ? '1200px' : '800px')};
`;

const ChartWithFixedTitles = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 60px 40px 60px; // 축 제목을 위한 여백 확보
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  /* ApexCharts 툴팁 z-index 최고로 설정 */
  .apexcharts-tooltip {
    z-index: 9999 !important;
  }
`;

const FixedAxisTitles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const XAxisTitle = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const YAxisTitle = styled.div`
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: center;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const FixedLegendContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 60px;
  right: 60px;
  z-index: 15;
  pointer-events: auto;
`;

const LineChartLegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
`;

const LineChartLegendItem = styled.div<{
  isHovered?: boolean;
  isHidden?: boolean;
  seriesColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 6px;
  opacity: ${({ isHidden }) => (isHidden ? 0.4 : 1)};
  background: ${({ isHovered, seriesColor }) =>
    isHovered
      ? `${seriesColor}15` // 시리즈 색상의 10% 투명도
      : 'transparent'};
  border: 1px solid
    ${({ isHovered, seriesColor }) => (isHovered ? seriesColor : 'transparent')};

  &:hover {
    background: ${({ seriesColor }) => `${seriesColor}15`};
    border-color: ${({ seriesColor }) => seriesColor};
    transform: translateY(-1px);
  }
`;

const LineChartLegendColor = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const LineChartLegendName = styled.span`
  white-space: nowrap;
`;

const CustomSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ active }) => (active ? palette.systemBlue : 'transparent')};
  color: ${({ active }) => (active ? 'white' : '#64748b')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;

  &:hover {
    background: ${({ active }) =>
      active ? palette.systemBlue : 'rgba(100, 116, 139, 0.1)'};
  }
`;

const NaviLayout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const RankingContainer = styled.div`
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  border-radius: 16px;
  padding: 1.2rem;
  box-shadow: 0 20px 40px rgba(75, 85, 99, 0.15),
    0 8px 24px rgba(75, 85, 99, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  height: fit-content;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100px;
    height: 100px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;

const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  z-index: 1;
`;

const RankingItem = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  border-radius: 12px;
  background: ${({ rank }) => {
    if (rank === 1)
      return 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)';
    if (rank === 2)
      return 'linear-gradient(135deg, #E5E4E2 0%, #C0C0C0 50%, #A8A8A8 100%)';
    if (rank === 3)
      return 'linear-gradient(135deg, #CD7F32 0%, #B8860B 50%, #A0750B 100%)';
    return 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)';
  }};
  color: ${({ rank }) => (rank <= 3 ? '#1a1a1a' : '#2d3748')};
  font-weight: ${({ rank }) => (rank <= 3 ? '700' : '600')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ rank }) => {
    if (rank <= 3) {
      return '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
    }
    return '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
  }};
  border: ${({ rank }) =>
    rank <= 3
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.3)'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ rank }) => {
      if (rank === 1)
        return 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)';
      if (rank === 2)
        return 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 100%)';
      if (rank === 3)
        return 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)';
      return 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)';
    }};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: ${({ rank }) => {
      if (rank <= 3) {
        return '0 8px 25px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
      }
      return '0 6px 20px rgba(0, 0, 0, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
    }};
  }
`;

const RankingNumber = styled.span<{ rank?: number }>`
  font-size: 1.2rem;
  font-weight: 900;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ rank }) => {
    if (rank === 1)
      return 'linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 100%)';
    if (rank === 2)
      return 'linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.2) 100%)';
    if (rank === 3)
      return 'linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 100%)';
    return 'linear-gradient(135deg, rgba(75, 85, 99, 0.3) 0%, rgba(107, 114, 128, 0.2) 100%)';
  }};
  color: ${({ rank }) => (rank && rank <= 3 ? '#1a1a1a' : 'white')};
  box-shadow: ${({ rank }) =>
    rank && rank <= 3
      ? 'inset 0 1px 2px rgba(255, 255, 255, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)'
      : 'inset 0 1px 2px rgba(255, 255, 255, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1)'};
  flex-shrink: 0;
`;

const RankingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const RankingName = styled.span<{ rank?: number }>`
  font-size: 0.9rem;
  font-weight: 700;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  line-height: 1.3;
  color: ${({ rank }) => (rank && rank <= 3 ? '#1a1a1a' : '#2d3748')};
  text-shadow: ${({ rank }) =>
    rank && rank <= 3
      ? '0 1px 2px rgba(0, 0, 0, 0.1)'
      : '0 1px 2px rgba(255, 255, 255, 0.8)'};
`;

const RankingCount = styled.span<{ rank?: number }>`
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  color: ${({ rank }) =>
    rank && rank <= 3 ? 'rgba(26, 26, 26, 0.8)' : 'rgba(45, 55, 72, 0.8)'};
  text-shadow: ${({ rank }) =>
    rank && rank <= 3
      ? '0 1px 2px rgba(0, 0, 0, 0.1)'
      : '0 1px 2px rgba(255, 255, 255, 0.6)'};
`;

const CustomLegendContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: visible; /* 아래로 펼쳐질 수 있도록 변경 */
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  margin-top: 1.5rem; /* 그래프와 범례 사이 간격 추가 */
  position: relative; /* LegendContent의 absolute 포지셔닝을 위해 */
`;

const LegendHeader = styled.div<{ expanded?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: ${
    ({ expanded }) =>
      expanded
        ? '8px 8px 0 0' /* 펼쳐졌을 때 상단만 둥글게 */
        : '8px' /* 접혔을 때 전체 둥글게 */
  };
  position: relative;
  z-index: 21; /* LegendContent보다 위에 */

  &:hover {
    background: #e9ecef;
  }
`;

const LegendTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const LegendToggle = styled.span<{ expanded: boolean }>`
  font-size: 0.8rem;
  color: #666;
  transition: transform 0.2s ease;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const LegendContent = styled.div<{ expanded: boolean }>`
  position: absolute;
  top: 100%; /* 헤더 바로 아래에서 시작 */
  left: 0;
  right: 0;
  z-index: 20; /* 다른 요소들 위에 표시 */
  max-height: ${({ expanded }) => (expanded ? '300px' : '0')};
  overflow-y: auto;
  transition: max-height 0.3s ease, box-shadow 0.3s ease;
  background: white;
  border-radius: 0 0 8px 8px; /* 아래쪽 모서리만 둥글게 */
  box-shadow: ${({ expanded }) =>
    expanded
      ? '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
      : 'none'};
  border: ${({ expanded }) => (expanded ? '1px solid #e5e7eb' : 'none')};
  border-top: none; /* 헤더와 연결되도록 상단 테두리 제거 */
`;

const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  padding: 1rem;
`;

const LegendItem = styled.div<{ isHovered: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ isHovered }) => (isHovered ? '#f0f8ff' : 'transparent')};
  border: 1px solid
    ${({ isHovered }) => (isHovered ? '#007bff' : 'transparent')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  &:hover {
    background: #f0f8ff;
    border-color: #007bff;
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

export default AvnfrGGDataContainer;
